import { type CancelToken } from 'axios'
import { logger } from 'shared/lib'
import {
  type IParamsSendFrom,
  type IParamsSendFromSmartOption,
  type ISendFromInfo,
} from 'entities/SendFrom/api/type'
import { type IIntegrationKey } from 'entities/Integrations'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { Phone } from 'entities/Phone/model/Phone'
import { InboxesApi } from 'entities/Inbox'
import { PhoneApi } from 'entities/Phone'
import { type IResponseInbox } from 'entities/Inbox/api/types'
import { type IResponseNumber } from 'entities/Phone/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'

type IconNumberType = 'aircall' | 'inboxes2'
type IconSmartInboxType = 'folder'

export const getSmartOptionLabel = (type?: IParamsSendFromSmartOption) => {
  switch (type) {
    case 'local_presence':
      return 'Local presence'
    case 'scaler':
      return 'Scaler'
    default:
      return ''
  }
}

const getSmartInboxInfo = (sendFrom: IParamsSendFrom, team?: Inbox) => {
  const teamId = sendFrom.options.team_id
  if (!teamId || !team) return null

  const { name } = team
  const value = getSmartOptionLabel(sendFrom.options?.smart_option)
  const icon: IconSmartInboxType = 'folder'

  return { name, value, icon }
}

const getNumberInfo = (sendFrom: IParamsSendFrom, number?: Phone, team?: Inbox) => {
  const numberId = sendFrom.options?.number_id
  if (!numberId) return null

  const name = number?.numberable?.name || team?.name
  const value = number?.formatted_number || team?.formatedNumbers[0]
  const icon: IconNumberType = number?.is_aircall ? 'aircall' : 'inboxes2'
  if (!name) return null

  return { name, value, icon }
}

const getContactOwnerInfo = () => {
  const icon: IconNumberType = 'inboxes2'
  return { name: 'Contact owner', icon }
}

const getIntegrationInfo = (integrationKey?: IIntegrationKey) => {
  if (!integrationKey) return null

  if (integrationKey === 'hubspot')
    return {
      icon: integrationKey,
      name: 'User property',
      label: '',
    }

  if (integrationKey === 'infusionsoft')
    return {
      icon: integrationKey,
      name: 'Lead owner',
      label: '',
    }

  return null
}

const fetchSendFromNecessaryData = async (
  sendFrom: IParamsSendFrom | null,
  cancelToken?: CancelToken
) => {
  if (!sendFrom) return {}
  const teamId = sendFrom.options?.team_id
  const numberId = sendFrom.options?.number_id
  let inboxData: IResponseInbox | null = null
  let numberData: IResponseNumber | null = null

  if (
    (sendFrom.type === 'from_smart_inbox' && teamId) ||
    (sendFrom.type === 'from_contact_owner' && teamId)
  ) {
    try {
      const { data } = await InboxesApi.getTeamById(teamId, { cancelToken })
      inboxData = data
    } catch (error) {
      if (sendFrom.type === 'from_contact_owner') {
        return { inboxData: null }
      }
      throw error
    }
  }

  if (sendFrom.type === 'from_number' && numberId) {
    const {
      data: { data: numbers },
    } = await PhoneApi.getNumbers({ ids: [numberId] }, { cancelToken })

    numberData = numbers[0]
  }

  return { inboxData, numberData }
}

export const getSendFromInfo = (
  sendFrom: IParamsSendFrom | null,
  options?: {
    integrationKey?: IIntegrationKey
    number?: Phone
    team?: Inbox
  }
): ISendFromInfo | null => {
  if (!sendFrom) return null

  try {
    switch (sendFrom.type) {
      case 'from_smart_inbox': {
        return getSmartInboxInfo(sendFrom, options?.team)
      }
      case 'from_number': {
        return getNumberInfo(sendFrom, options?.number, options?.team)
      }
      case 'from_integration': {
        return getIntegrationInfo(options?.integrationKey)
      }
      case 'from_contact_owner': {
        const isSmartInbox = options?.team?.is_smart

        if (options?.team) {
          if (isSmartInbox) {
            return getSmartInboxInfo(sendFrom, options?.team)
          } else {
            return getNumberInfo(
              { ...sendFrom, options: { ...sendFrom.options, number_id: options?.team?.numberId } },
              options?.team?.number,
              options?.team
            )
          }
        }
        return getContactOwnerInfo()
      }
      default:
        return null
    }
  } catch (error) {
    logger.error(error)

    return null
  }
}

export const fetchSendFromInfo = async (
  sendFrom: IParamsSendFrom | null,
  entities: IEntitiesStore,
  cancelToken?: CancelToken
) => {
  const { inboxData, numberData } = await fetchSendFromNecessaryData(sendFrom, cancelToken)

  return getSendFromInfo(sendFrom, {
    number: numberData ? new Phone(numberData, entities) : undefined,
    team: inboxData ? new Inbox(inboxData, entities) : undefined,
  })
}
